// Single choke point for validating a Descope `baseUrl`. Every place that
// accepts a baseUrl from an untrusted source (URL query params, localStorage,
// the Settings form) must run the value through `sanitizeBaseUrl` before it
// is ever used to fetch config.json/theme.json/screen HTML or rendered.
//
// Background: explorer.descope.com used to forward the `baseUrl`/`baseURL`
// query param straight to @descope/web-component with only a trim/null-check.
// A link like `?baseUrl=https://attacker.example` made the widget fetch its
// flow config and screen HTML (assigned to `template.innerHTML`) from an
// attacker-controlled origin while a real Descope domain with a valid cert
// stayed in the address bar - a content-injection / credential-phishing
// vector. Fix: only ever accept Descope-operated hosts.
//
// Keep ALLOWED_HOSTS / ALLOWED_SUFFIXES in sync with the CSP `connect-src`
// allowlist in vercel.json - they describe the same trust boundary.

// https://docs.descope.com/management/project-settings/multi-regional
const REGIONS = [
  { id: "us", label: "US - Virginia (default)", baseUrl: "https://api.descope.com" },
  { id: "eu", label: "EU - Frankfurt", baseUrl: "https://api.euc1.descope.com" },
  { id: "au", label: "AU - Sydney", baseUrl: "https://api.aps2.descope.com" },
  { id: "ca", label: "CA - Montreal", baseUrl: "https://api.cac1.descope.com" },
];

const DEFAULT_BASE_URL = REGIONS[0].baseUrl;

// Exact hostnames of Descope's public regional API hosts.
const ALLOWED_HOSTS = new Set(REGIONS.map((region) => new URL(region.baseUrl).hostname));

// Descope-operated wildcard domains used for private-cloud/sandbox ("star")
// tenants, e.g. https://api.star5.descope.app (see README "Deploying on
// Vercel and CSP" section). Matched as a subdomain suffix only - never the
// bare apex, and never a lookalike host such as "evil-descope.app".
const ALLOWED_SUFFIXES = [".descope.app", ".descope.org"];

const isAllowedHost = (hostname) => {
  const host = String(hostname || "").toLowerCase();
  if (ALLOWED_HOSTS.has(host)) return true;
  return ALLOWED_SUFFIXES.some((suffix) => host.endsWith(suffix));
};

// Validates an untrusted baseUrl string and returns its normalized origin
// (scheme + host, no path/query/fragment), or null if it isn't a well-formed
// https:// URL on an allowed Descope-operated host.
const sanitizeBaseUrl = (raw) => {
  if (raw === undefined || raw === null) return null;

  const trimmed = String(raw).trim();
  if (trimmed === "") return null;

  let url;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  if (url.protocol !== "https:") return null;
  if (url.username || url.password) return null;
  if (!isAllowedHost(url.hostname)) return null;
  if (url.port !== "") return null;

  return url.origin;
};

export { REGIONS, DEFAULT_BASE_URL, isAllowedHost, sanitizeBaseUrl };
